import { useState, useEffect, useCallback, useRef } from "react";
import { todayLocal } from "../utils/dateLocal";
import axios from "axios";
import { FilePlus, Save, Printer, Handshake, DoorClosed } from "lucide-react";
import { openReport, buildReportName } from "../lib/reportViewerBus";
import DateInput from "./DateInput";
import { WinButton } from "./WinComponents";
import EntenteModal from "./EntenteModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal Refus — parité stricte VB.NET `frmRefus` + `CodeRefus.vb`.
 * -----------------------------------------------------------------------------
 * Structure legacy (3 onglets, ouverture par défaut sur Liste s'il y a des refus) :
 *   Tab 1 (Demande d'aide juridique) : 2 grilles teal Disponibles ↔ Sélectionnées
 *   Tab 2 (Refus)                    : Nature (200 chars), Raison (long), Avocat,
 *                                       Dates émis/effectif, Montant à recouvrer,
 *                                       Entente Oui/Non, Copie conforme
 *   Tab 3 (Liste)                    : Historique refus (Ouvrir / Supprimer)
 */

const TEAL_HEADER = "#0d7377";

// Bandeau de section teal (parité captures legacy : Requérant, Volet contributif…)
const SectionHeader = ({ title }) => (
  <div style={{
    background: TEAL_HEADER, color: "#fff", padding: "3px 8px",
    fontSize: 12, fontWeight: 600, textAlign: "center",
  }}>{title}</div>
);

const EMPTY_REFUS = {
  adredateemis: todayLocal(),
  adredaterefus: todayLocal(),
  adrenature: "",
  adreraison: "",
  adrecodeavosign: "",
  adreavonomsign: "",
  adreavoprenomsign: "",
  adremontrecouv: "0.00",
  adreentente: 0,
  adrenomavomand: "",
};

const GRID_COLS_DISPO = [
  { key: "noseqdaj", label: "No.\u00A0séq.", width: 100 },
  { key: "no_doss_cour", label: "No. Doss.Cour", width: 100 },
  { key: "nature_code", label: "Nature", width: 70 },
  { key: "avocat_code", label: "Avocat", width: 70 },
  { key: "dem_def", label: "Dem/Def", width: 80 },
  { key: "decision", label: "Décision", width: 90 },
];

const GRID_COLS_LISTE = [
  { key: "date_emis", label: "Date", width: 100 },
  { key: "noref", label: "No. Référence", width: 130 },
  { key: "montant", label: "Montant Recouv", width: 130 },
  { key: "no_dossier", label: "No. Dossier", width: 120 },
];

const RefusModal = ({ isOpen, onClose, noclient, showPopup, formData,
                     requerantNom, requerantPrenom, onRefusChanged, canSupprimer = false }) => {
  const [activeTab, setActiveTab] = useState("liste");
  const [form, setForm] = useState({ ...EMPTY_REFUS });
  const [dispoRows, setDispoRows] = useState([]);
  const [selecRows, setSelecRows] = useState([]);
  const [dispoSelIdx, setDispoSelIdx] = useState(-1);
  const [selecSelIdx, setSelecSelIdx] = useState(-1);
  const [refus, setRefus] = useState([]);
  const [listeSelIdx, setListeSelIdx] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [pwdSuppr, setPwdSuppr] = useState("");
  const [showPwdSuppr, setShowPwdSuppr] = useState(false);
  const [currentNoref, setCurrentNoref] = useState(""); // refus ouvert/enregistré
  const [enteneModal, setEnteneModal] = useState(null);  // {noref, regbur} | null
  const skipEffectRef = useRef(false);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadEligibles = useCallback(async () => {
    if (!noclient) return;
    try {
      const { data } = await axios.get(`${API}/clients/${noclient}/daj-eligibles-refus`);
      setDispoRows(data || []);
    } catch { setDispoRows([]); }
  }, [noclient]);

  const loadRefus = useCallback(async () => {
    if (!noclient) return;
    try {
      const { data } = await axios.get(`${API}/clients/${noclient}/refus`);
      setRefus((data || []).map(r => ({
        ...r,
        date_emis: (r.adredateemis || "").substring(0, 10),
        montant: `${parseFloat(r.adremontrecouv || 0).toFixed(2)}$`,
        no_dossier: r.nodossier || "-",
      })));
    } catch { setRefus([]); }
  }, [noclient]);

  // Ouverture initiale de la modale : reset + charge données
  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...EMPTY_REFUS });
    setSelecRows([]); setDispoSelIdx(-1); setSelecSelIdx(-1); setListeSelIdx(-1);
    loadEligibles();
    loadRefus();
  }, [isOpen, noclient, loadEligibles, loadRefus]);

  // Auto-ouverture du refus existant : voir useEffect après openRefusByNoref.

  // Actions transfert grille (parité `AjoutSelectRef` / `RetireSelecRefus`)
  const doAjout = async () => {
    if (dispoSelIdx < 0 || dispoSelIdx >= dispoRows.length) return;
    const row = dispoRows[dispoSelIdx];
    // Vérif avocat identique (parité `AjoutSelectRef` ligne 482)
    if (selecRows.length > 0 && selecRows[0].avocat_code !== row.avocat_code) {
      showPopup?.("Les demandes sélectionnées doivent avoir le même avocat...", "warning", "Refus");
      return;
    }
    const nextSelec = [...selecRows, row];
    setSelecRows(nextSelec);
    setDispoRows(dispoRows.filter((_, i) => i !== dispoSelIdx));
    setDispoSelIdx(-1);
    // Auto-remplissage avocat signataire depuis le DAJ (parité `AffecteAvoRefus`)
    if (selecRows.length === 0) {
      setForm(p => ({
        ...p,
        adrecodeavosign: row.avocat_signataire_code || row.avocat_code || "",
        adrenomavomand: row.adda706 || "",
      }));
    }
    // Auto-composition Nature + Raison + Avocat signataire (parité subDsRaisonRefus)
    // Appelle le backend qui compose depuis natures/natserv + flags DAJ.
    try {
      const seqList = nextSelec.map(r => r.noseqdaj);
      const langue = (formData?.langue === '0' || formData?.langue === 0) ? 0 : 1;
      const { data } = await axios.post(`${API}/refus/compute-refus-text`,
        { noseqdaj_list: seqList, langue });
      setForm(p => ({
        ...p,
        adrenature: (data.nature || "").slice(0, 200),
        adreraison: data.raison || "",
        adreavonomsign: (() => {
          const lbl = data.avocat_signataire_label || "";
          const parts = lbl.replace(/^Me\s+/, "").split(" ");
          return parts.slice(1).join(" ") || parts[0] || p.adreavonomsign;
        })(),
        adreavoprenomsign: (() => {
          const lbl = data.avocat_signataire_label || "";
          const parts = lbl.replace(/^Me\s+/, "").split(" ");
          return parts[0] || p.adreavoprenomsign;
        })(),
      }));
    } catch (e) {
      // Silencieux — les champs peuvent rester vides si compute échoue
    }
    // Auto-bascule sur onglet Refus après 1er ajout
    if (selecRows.length === 0) setTimeout(() => setActiveTab("refus"), 100);
  };
  const doEnleve = () => {
    if (selecSelIdx < 0 || selecSelIdx >= selecRows.length) return;
    const row = selecRows[selecSelIdx];
    setDispoRows([...dispoRows, row]);
    setSelecRows(selecRows.filter((_, i) => i !== selecSelIdx));
    setSelecSelIdx(-1);
  };
  const doAjoutTout = async () => {
    if (dispoRows.length === 0) return;
    const avo0 = dispoRows[0].avocat_code;
    if (!dispoRows.every(r => r.avocat_code === avo0)) {
      showPopup?.("Les demandes sélectionnées doivent avoir le même avocat...", "warning", "Refus");
      return;
    }
    const nextSelec = [...selecRows, ...dispoRows];
    setSelecRows(nextSelec);
    const firstAdded = dispoRows[0];
    if (selecRows.length === 0 && dispoRows.length > 0) {
      setForm(p => ({
        ...p,
        adrecodeavosign: firstAdded.avocat_signataire_code || firstAdded.avocat_code || "",
        adrenomavomand: firstAdded.adda706 || "",
      }));
      setTimeout(() => setActiveTab("refus"), 100);
    }
    setDispoRows([]);
    // Auto-composition Nature + Raison + Avocat (parité subDsRaisonRefus)
    try {
      const seqList = nextSelec.map(r => r.noseqdaj);
      const langue = (formData?.langue === '0' || formData?.langue === 0) ? 0 : 1;
      const { data } = await axios.post(`${API}/refus/compute-refus-text`,
        { noseqdaj_list: seqList, langue });
      setForm(p => ({
        ...p,
        adrenature: (data.nature || "").slice(0, 200),
        adreraison: data.raison || "",
      }));
    } catch (e) { /* silencieux */ }
  };
  const doEnleveTout = () => {
    setDispoRows([...dispoRows, ...selecRows]);
    setSelecRows([]);
  };

  // Nouveau refus (parité `ReqNouvRefus`)
  const doNouveau = () => {
    setForm({ ...EMPTY_REFUS });
    setSelecRows([]);
    setCurrentNoref("");
    loadEligibles();
    setActiveTab("demande");
  };

  const doSave = async () => {
    setSaving(true);
    try {
      // Validations legacy `ReqEnrgRefus`
      if (!form.adrecodeavosign.trim()) {
        showPopup?.("Le champ : code d'avocat sign. ne peut être vide.", "warning", "Refus");
        return;
      }
      if (selecRows.length === 0) {
        showPopup?.("Le champ : Demandes sélectionnées ne peut être vide.", "warning", "Refus");
        return;
      }
      if (form.adredaterefus > form.adredateemis) {
        showPopup?.("La date effective du refus ne peut être supérieure à la date émise.", "warning", "Refus");
        return;
      }
      const payload = {
        noclient,
        noseqdaj_list: selecRows.map(r => r.noseqdaj),
        adredateemis: form.adredateemis,
        adredaterefus: form.adredaterefus,
        adrenature: form.adrenature,
        adreraison: form.adreraison,
        adrecodeavosign: form.adrecodeavosign,
        adreavonomsign: form.adreavonomsign,
        adreavoprenomsign: form.adreavoprenomsign,
        adremontrecouv: parseFloat(form.adremontrecouv.toString().replace(/[^\d.-]/g, "")) || 0,
        adreentente: form.adreentente || 0,
        adrenomavomand: form.adrenomavomand,
        adrereqnom: requerantNom || "",
        adrereqprenom: requerantPrenom || "",
        adrereqadr: formData?.adresse || "",
        adrereqville: formData?.ville || "",
        adrereqprov: formData?.province || "QC",
        adrereqcodepostal: formData?.code_postal || "",
        adrereqdatenais: formData?.date_naissance || null,
      };
      const { data } = await axios.post(`${API}/refus`, payload);
      showPopup?.(`Refus ${data.noref} créé avec succès.\n${data.nb_daj} DAJ lié(s).`, "success", "Refus");
      if (onRefusChanged) onRefusChanged(data.noref, payload.noseqdaj_list);
      loadRefus(); loadEligibles();
      setForm({ ...EMPTY_REFUS });
      setSelecRows([]);
      setActiveTab("liste");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.blocages) {
        const msg = detail.blocages.map(b => `${b.noseqdaj} :\n  - ${b.raisons.join("\n  - ")}`).join("\n\n");
        showPopup?.(`${detail.message}\n\n${msg}`, "error", "Refus impossible");
      } else {
        showPopup?.(`Erreur : ${detail || e.message}`, "error", "Refus");
      }
    } finally { setSaving(false); }
  };

  const openRefusByNoref = useCallback(async (noref, silent = false) => {
    if (!noref) return;
    try {
      const { data } = await axios.get(`${API}/refus/${encodeURIComponent(noref)}`);
      const r = data.refus || {};
      skipEffectRef.current = true;
      setForm({
        adredateemis: (r.adredateemis || "").substring(0, 10),
        adredaterefus: (r.adredaterefus || "").substring(0, 10),
        adrenature: (r.adrenature || "").trim(),
        adreraison: (r.adreraison || "").trim(),
        adrecodeavosign: (r.adrecodeavosign || "").trim(),
        adreavonomsign: (r.adreavonomsign || "").trim(),
        adreavoprenomsign: (r.adreavoprenomsign || "").trim(),
        adremontrecouv: parseFloat(r.adremontrecouv || 0).toFixed(2),
        adreentente: r.adreentente || 0,
        adrenomavomand: (r.adrenomavomand || "").trim(),
      });
      const liens = data.dajs_lies || [];
      setSelecRows(liens.map(d => ({
        noseqdaj: d.noseqdaj || "",
        no_doss_cour: d.adrenocause || "",
        nature_code: d.adrecodenature || "",
        avocat_code: d.adda712 || "",
        avocat_signataire_code: d.adda704 || "",
        dem_def: d.adda540 === 1 ? "En demande" : "En défense",
        decision: d.adredecision === "REF" ? "Refus" : d.adredecision === "RCS" ? "Ref.consult." : "Ref.contrib.",
      })));
      setDispoRows([]);
      setActiveTab("refus");
      setCurrentNoref((noref || "").trim());
      if (!silent) showPopup?.(`Refus ${noref} ouvert pour consultation.`, "success", "Refus");
    } catch (e) {
      if (!silent) showPopup?.(`Erreur ouverture : ${e.response?.data?.detail || e.message}`, "error", "Refus");
    }
  }, [showPopup]);

  const doOuvrir = () => {
    if (listeSelIdx < 0) return;
    openRefusByNoref(refus[listeSelIdx]?.noref, false);
  };

  // regbur du refus courant (NoReg 2c + NoBur 2c) — parité bouton Imprimer.
  const refusRegbur = () => {
    const li = refus[listeSelIdx] || {};
    const rr = String(li.noreg || formData?.region || "").padStart(2, "0").slice(0, 2);
    const bb = String(li.nobur || formData?.bureau || "").padStart(2, "0").slice(0, 2);
    return rr + bb;
  };

  // Bouton/entente activé si l'entente est à « Oui » ET le refus est enregistré
  // (noref présent), parité legacy CodeRefus.vb (BtnEntente.Enabled = adreentente==1).
  const enteneEnabled = form.adreentente === 1 && !!currentNoref;

  // Ouvre l'Entente (parité frmRefus.showEntente) : exige un refus enregistré.
  const openEntente = () => {
    if (!currentNoref) {
      showPopup?.("Vous devez enregistrer le refus avant de saisir une entente de paiement.",
                  "warning", "Entente de paiement");
      return;
    }
    setEnteneModal({ noref: currentNoref, regbur: refusRegbur() });
  };

  // Sélection « Oui » : active + ouvre l'Entente (parité RadYes_Click → PerformClick).
  const onEntenteOui = () => {
    setField("adreentente", 1);
    if (currentNoref) setEnteneModal({ noref: currentNoref, regbur: refusRegbur() });
    else showPopup?.("Vous devez enregistrer le refus avant de saisir une entente de paiement.",
                     "warning", "Entente de paiement");
  };

  // Sélection « Non » : supprime l'entente existante (parité RadNo → supprimerEntente).
  const onEntenteNon = async () => {
    setField("adreentente", 0);
    if (currentNoref) {
      try { await axios.delete(`${API}/refus/${encodeURIComponent(currentNoref)}/entente`); }
      catch (e) { /* idempotent : 404 ignoré */ }
    }
  };

  // Auto-ouverture : à l'ouverture de la modale, si un refus existe on l'affiche
  // directement (onglet Refus peuplé) sans passer par « Ouvrir ». On privilégie
  // le refus correspondant au No. Référence du dossier courant, sinon le 1er.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { autoOpenedRef.current = false; return; }
    if (autoOpenedRef.current) return;
    if (!refus || refus.length === 0) { setActiveTab("demande"); return; }
    autoOpenedRef.current = true;
    const curRef = (formData?.no_reference || "").trim();
    const match = curRef ? refus.find(x => (x.noref || "").trim() === curRef) : null;
    const target = match || refus[0];
    setListeSelIdx(refus.findIndex(x => x.noref === target.noref));
    openRefusByNoref(target.noref, true);
  }, [isOpen, refus, formData?.no_reference, openRefusByNoref]);

  const doSupprimer = async () => {
    if (listeSelIdx < 0) return;
    const noref = refus[listeSelIdx]?.noref;
    if (!noref) return;
    const noreg = formData?.region || "90";
    try {
      const url = `${API}/refus/${encodeURIComponent(noref)}?num_region=${noreg}&motdepasse=${encodeURIComponent(pwdSuppr)}`;
      const res = await axios.delete(url);
      showPopup?.(res.data.message, "success", "Suppression");
      setShowPwdSuppr(false); setPwdSuppr("");
      setForm({ ...EMPTY_REFUS }); setSelecRows([]); setListeSelIdx(-1); setActiveTab("liste");
      loadRefus(); loadEligibles();
      if (onRefusChanged) onRefusChanged("", []);
    } catch (e) {
      showPopup?.(`${e.response?.data?.detail || e.message}`, "error", "Suppression");
    }
  };

  if (!isOpen) return null;
  const clientTitle = [requerantPrenom, requerantNom].filter(Boolean).join(" ");

  // Rendu grille générique
  const renderGrid = (rows, cols, selIdx, setSel, testIdPrefix, height = 180) => (
    <div style={{ background: "#fff", border: "1px solid #808080", height, overflow: "auto" }}>
      <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
        <thead style={{ position: "sticky", top: 0 }}>
          <tr>{cols.map(c => (
            <th key={c.key} style={{
              background: TEAL_HEADER, color: "#fff", padding: "4px 6px",
              textAlign: "left", fontSize: 11, width: c.width, borderRight: "1px solid #095457",
            }}>{c.label}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} style={{ padding: 20, textAlign: "center", color: "#999" }}>
              {testIdPrefix === "dispo" ? "Aucun dossier n'est disponible pour émettre un refus." : "\u00A0"}
            </td></tr>
          )}
          {rows.map((r, idx) => (
            <tr key={r.noseqdaj || r.noref || idx}
                onClick={() => setSel(idx)}
                onDoubleClick={() => { if (testIdPrefix === "refus-liste") { setSel(idx); setTimeout(doOuvrir, 0); } }}
                style={{ background: idx === selIdx ? "#316ac5" : "#fff", color: idx === selIdx ? "#fff" : "#000", cursor: "pointer" }}
                data-testid={`refus-${testIdPrefix}-row-${idx}`}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: "3px 6px", borderBottom: "1px solid #eee" }}>{r[c.key] ?? "-"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="popup-overlay" data-testid="modal-refus" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#dbe6f0", border: "1px solid #808080", boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        width: "min(1180px, 98vw)", height: "min(760px, 96vh)", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {/* Titre */}
        <div style={{
          background: "linear-gradient(to bottom, #dbe6f0, #b8ccd8)",
          padding: "4px 8px", fontSize: 12, fontWeight: 600, color: "#111",
          borderBottom: "1px solid #808080", display: "flex", justifyContent: "space-between",
        }}>
          <span data-testid="refus-titre">Refus  {clientTitle}</span>
          <button onClick={onClose} data-testid="btn-refus-close-x"
                  style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>×</button>
        </div>

        {/* Toolbar */}
        <div className="win-toolbar" style={{ padding: "8px 12px", gap: 8 }}>
          <WinButton IconComponent={FilePlus} onClick={doNouveau} title="Nouveau refus" data-testid="btn-refus-nouveau">Nouveau</WinButton>
          <WinButton IconComponent={Save} onClick={doSave} disabled={saving || selecRows.length === 0} title="Enregistrer" data-testid="btn-refus-save">
            {saving ? "..." : "Enregistrer"}
          </WinButton>
          <WinButton
            IconComponent={Printer}
            disabled={!(refus[listeSelIdx]?.noref)}
            onClick={() => {
              const nrf = refus[listeSelIdx]?.noref;
              if (!nrf) {
                showPopup?.("Sélectionnez d'abord un refus dans l'onglet Liste.",
                            "warning", "Imprimer");
                return;
              }
              // regbur = NoReg (2c) + NoBur (2c) — priorité à la ligne
              // sélectionnée, fallback au header dossier
              const rr = String(refus[listeSelIdx]?.noreg
                          || formData?.region || "").padStart(2, "0").slice(0, 2);
              const bb = String(refus[listeSelIdx]?.nobur
                          || formData?.bureau || "").padStart(2, "0").slice(0, 2);
              const url = `${API}/refus-pdf`
                + `?noref=${encodeURIComponent(nrf)}`
                + `&regbur=${encodeURIComponent(rr + bb)}`;
              openReport({ url, title: "Refus",
                fileName: buildReportName("REFUS", nrf, requerantPrenom, requerantNom) });
            }}
            title="Imprimer le refus sélectionné (PDF, ouvre dans un nouvel onglet)"
            data-testid="btn-refus-print">Imprimer</WinButton>
          <WinButton
            IconComponent={Printer}
            disabled={!(refus[listeSelIdx]?.noref)}
            onClick={() => {
              const nrf = refus[listeSelIdx]?.noref;
              if (!nrf) {
                showPopup?.("Sélectionnez d'abord un refus dans l'onglet Liste.",
                            "warning", "Demande de révision");
                return;
              }
              const rr = String(refus[listeSelIdx]?.noreg
                          || formData?.region || "").padStart(2, "0").slice(0, 2);
              const bb = String(refus[listeSelIdx]?.nobur
                          || formData?.bureau || "").padStart(2, "0").slice(0, 2);
              const url = `${API}/revision-pdf`
                + `?noref=${encodeURIComponent(nrf)}`
                + `&regbur=${encodeURIComponent(rr + bb)}`;
              openReport({ url, title: "Demande de révision",
                fileName: buildReportName("REVISION", nrf, requerantPrenom, requerantNom) });
            }}
            title="Imprimer la Demande de révision pré-remplie pour le refus sélectionné"
            data-testid="btn-refus-revision">Demande de révision</WinButton>
          <WinButton IconComponent={Handshake} onClick={openEntente} disabled={!enteneEnabled}
                     title={enteneEnabled ? "Entente de paiement" : "Sélectionnez « Oui » sur un refus enregistré"}
                     data-testid="btn-refus-entente">Entente</WinButton>
          <div className="win-toolbar-separator" />
          <WinButton IconComponent={DoorClosed} onClick={onClose} title="Fermer" data-testid="btn-refus-close">Fermer</WinButton>
        </div>

        {/* En-tête dossier : No. Dossier · No. Référence · Région · Bureau · Date émise
            (parité capture legacy frmRefus). Champs gris non éditables sauf Date émise. */}
        <div style={{ background: "#dbe6f0", padding: "6px 8px", display: "grid",
                      gridTemplateColumns: "1fr 1fr 80px 80px 150px", gap: 10,
                      borderBottom: "1px solid #a0a0a0" }}>
          {[
            { lbl: "No. Dossier", val: formData?.no_dossier || "", testId: "refus-hdr-nodossier" },
            { lbl: "No. Référence", val: formData?.no_reference || "", testId: "refus-hdr-noref" },
            { lbl: "Région", val: formData?.region || "", testId: "refus-hdr-region" },
            { lbl: "Bureau", val: formData?.bureau || "", testId: "refus-hdr-bureau" },
          ].map(f => (
            <div key={f.lbl} style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#000" }}>{f.lbl}</label>
              <input value={f.val} readOnly data-testid={f.testId}
                     style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 12, background: "#e0e0e0" }} />
            </div>
          ))}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#000" }}>Date émise</label>
            <DateInput value={form.adredateemis} onChange={v => setField("adredateemis", v)} testId="refus-hdr-dateemis" />
          </div>
        </div>

        {/* Onglets */}
        <div style={{ display: "flex", background: "#c0d0dc", borderBottom: "1px solid #808080" }}>
          {[
            { id: "demande", label: "Demande d'aide juridique" },
            { id: "refus", label: "Refus" },
            { id: "liste", label: "Liste" },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
                    data-testid={`refus-tab-${t.id}`}
                    style={{
                      padding: "6px 16px", fontSize: 12, cursor: "pointer",
                      background: activeTab === t.id ? "#dbe6f0" : "transparent",
                      border: "1px solid #808080", borderBottom: activeTab === t.id ? "none" : "1px solid #808080",
                      fontWeight: activeTab === t.id ? 700 : 400,
                    }}>{t.label}</button>
          ))}
        </div>

        {/* Contenu — hauteur fixe, scroll interne pour que le modal ne change
            pas de taille en changeant d'onglet (parité Attestation). */}
        <div style={{ padding: 12, background: "#dbe6f0", flex: 1, minHeight: 0, overflow: "auto" }}>
          {activeTab === "demande" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* Requérant */}
              <div style={{ border: "1px solid #808080" }}>
                <SectionHeader title="Requérant" />
                <div style={{ background: "#dbe6f0", padding: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 80px 1fr 150px 150px", gap: 6, alignItems: "center", marginBottom: 4 }}>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Nom :</label>
                    <input value={requerantNom || ""} readOnly data-testid="refus-req-nom"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 12, background: "#e0e0e0" }} />
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Prénom :</label>
                    <input value={requerantPrenom || ""} readOnly data-testid="refus-req-prenom"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 12, background: "#e0e0e0" }} />
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Date de naissance :</label>
                    <input value={(formData?.date_naissance || "").substring(0, 10)} readOnly data-testid="refus-req-datenais"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 12, background: "#e0e0e0" }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 6, alignItems: "center" }}>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Adresse :</label>
                    <input value={[formData?.adresse, formData?.ville, formData?.province && `(${formData.province})`, formData?.code_postal].filter(Boolean).join("  ")}
                           readOnly data-testid="refus-req-adresse"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 12, background: "#e0e0e0" }} />
                  </div>
                </div>
              </div>

              {/* Grilles Disponibles ↔ Sélectionnées */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 1fr", gap: 10, alignItems: "stretch" }}>
                <div style={{ border: "1px solid #808080" }}>
                  <SectionHeader title="Demandes disponibles" />
                  {renderGrid(dispoRows, GRID_COLS_DISPO, dispoSelIdx, setDispoSelIdx, "dispo", 190)}
                </div>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
                  <button onClick={doAjout} disabled={dispoSelIdx < 0} className="win-btn" data-testid="btn-refus-ajout" title="Ajouter">-&gt;</button>
                  <button onClick={doAjoutTout} disabled={dispoRows.length === 0} className="win-btn" data-testid="btn-refus-ajout-tout" title="Ajouter tout">&raquo;</button>
                  <button onClick={doEnleve} disabled={selecSelIdx < 0} className="win-btn" data-testid="btn-refus-enleve" title="Retirer">&lt;-</button>
                  <button onClick={doEnleveTout} disabled={selecRows.length === 0} className="win-btn" data-testid="btn-refus-enleve-tout" title="Retirer tout">&laquo;</button>
                </div>
                <div style={{ border: "1px solid #808080" }}>
                  <SectionHeader title="Demandes sélectionnées" />
                  {renderGrid(selecRows, GRID_COLS_DISPO, selecSelIdx, setSelecSelIdx, "selec", 190)}
                </div>
              </div>

              {/* Volet contributif */}
              <div style={{ border: "1px solid #808080" }}>
                <SectionHeader title="Volet contributif" />
                <div style={{ background: "#dbe6f0", padding: 12, display: "grid", gridTemplateColumns: "1fr 260px", gap: 20, alignItems: "center" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "220px 140px", gap: 6, alignItems: "center" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, border: "1px solid #808080", padding: "3px 8px", background: "#dbe6f0" }}>Montant du recouvrement :</label>
                    <input value={form.adremontrecouv}
                           onChange={e => setField("adremontrecouv", e.target.value)}
                           data-testid="refus-montant"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 12, textAlign: "right" }} />
                  </div>
                  <fieldset style={{ border: "1px solid #808080", padding: 8, margin: 0 }}>
                    <legend style={{ fontSize: 12, padding: "0 4px", fontWeight: 600 }}>Entente de paiement</legend>
                    <label style={{ fontSize: 12, marginRight: 12 }}>
                      <input type="radio" name="refus-entente" checked={form.adreentente === 1}
                             onChange={onEntenteOui} data-testid="refus-entente-oui" /> Oui
                    </label>
                    <label style={{ fontSize: 12 }}>
                      <input type="radio" name="refus-entente" checked={form.adreentente === 0}
                             onChange={onEntenteNon} data-testid="refus-entente-non" /> Non
                    </label>
                  </fieldset>
                </div>
              </div>
            </div>
          )}

          {activeTab === "refus" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
              <div>
                <div style={{ background: TEAL_HEADER, color: "#fff", padding: "3px 8px", fontSize: 11, fontWeight: 600 }}>
                  Nature et objet du dossier (max 200 caractères)
                </div>
                <textarea value={form.adrenature} maxLength={200}
                          onChange={e => setField("adrenature", e.target.value)}
                          data-testid="refus-nature"
                          style={{ width: "100%", height: 46, border: "1px solid #808080", padding: 4, fontSize: 12, fontFamily: "inherit", resize: "vertical" }} />
                {/* Indicateur visuel de longueur — limite legacy 200 caractères
                    (parité frmRefus.Designer.vb: `txtAdReNature.MaxLength = 200`).
                    Couleurs progressives : gris (0-180) → orange (181-195) → rouge (196-200). */}
                <div data-testid="refus-nature-count"
                     style={{
                       fontSize: 11, fontWeight: 600, textAlign: "right",
                       padding: "2px 4px",
                       color: form.adrenature.length > 195 ? "#d13438"
                            : form.adrenature.length > 180 ? "#e88a1a"
                            : "#666",
                     }}>
                  {form.adrenature.length} / 200 caractères
                  {form.adrenature.length >= 200 && (
                    <span style={{ marginLeft: 8, fontWeight: 700 }}>⚠ Limite atteinte</span>
                  )}
                </div>
              </div>
              <div>
                <div style={{ background: TEAL_HEADER, color: "#fff", padding: "3px 8px", fontSize: 11, fontWeight: 600 }}>
                  Raison du refus
                </div>
                <textarea value={form.adreraison}
                          onChange={e => setField("adreraison", e.target.value)}
                          data-testid="refus-raison"
                          style={{ width: "100%", height: 84, border: "1px solid #808080", padding: 4, fontSize: 12, fontFamily: "inherit", resize: "vertical" }} />
              </div>
              {/* Boutons legacy (parité `btnFondDroit_Click` + `btnNotes_Click`
                  — CodeRefus.vb:1479 et 1485). Ajout du fondement du droit à la
                  Nature et ajout des notes à la Raison. */}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="win-btn" data-testid="btn-refus-fond-droit"
                        onClick={() => {
                          const natAcces = (formData?.service?.nature_accessoire || formData?.nature_accessoire || "").trim();
                          const fondDroit = (formData?.service?.fondement_droit || formData?.fondement_droit || "").trim();
                          if (!natAcces && !fondDroit) {
                            showPopup?.("Aucun fondement du droit à ajouter (les champs Nature accessoire et Fondement du droit sont vides dans le dossier).",
                                        "info", "Refus");
                            return;
                          }
                          const addition = `${natAcces ? "   " + natAcces : ""}${fondDroit ? "   " + fondDroit : ""}`;
                          setField("adrenature", (form.adrenature + addition).slice(0, 200));
                        }}>Ajouter fondement du droit</button>
                <button className="win-btn" data-testid="btn-refus-notes"
                        onClick={() => {
                          const notes = (formData?.notes || "").trim();
                          if (!notes) {
                            showPopup?.("Aucune note à ajouter (le champ Notes du dossier est vide).",
                                        "info", "Refus");
                            return;
                          }
                          setField("adreraison", form.adreraison + "   " + notes);
                        }}>Ajouter les notes</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Date émission</label>
                  <DateInput value={form.adredateemis} onChange={v => setField("adredateemis", v)} testId="refus-dateemis" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Effectif le</label>
                  <DateInput value={form.adredaterefus} onChange={v => setField("adredaterefus", v)} testId="refus-daterefus" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Code avocat signataire *</label>
                  <input value={form.adrecodeavosign} maxLength={6}
                         onChange={e => setField("adrecodeavosign", e.target.value.toUpperCase())}
                         data-testid="refus-avosign-code"
                         style={{ width: "100%", border: "1px solid #808080", padding: "3px 5px", fontSize: 12 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Nom avocat mandataire</label>
                  <input value={form.adrenomavomand} maxLength={40}
                         onChange={e => setField("adrenomavomand", e.target.value)}
                         data-testid="refus-avomand-nom"
                         style={{ width: "100%", border: "1px solid #808080", padding: "3px 5px", fontSize: 12 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Nom avocat signataire</label>
                  <input value={form.adreavonomsign} maxLength={40}
                         onChange={e => setField("adreavonomsign", e.target.value)}
                         data-testid="refus-avosign-nom"
                         style={{ width: "100%", border: "1px solid #808080", padding: "3px 5px", fontSize: 12 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Prénom avocat signataire</label>
                  <input value={form.adreavoprenomsign} maxLength={40}
                         onChange={e => setField("adreavoprenomsign", e.target.value)}
                         data-testid="refus-avosign-prenom"
                         style={{ width: "100%", border: "1px solid #808080", padding: "3px 5px", fontSize: 12 }} />
                </div>
              </div>
            </div>
          )}

          {activeTab === "liste" && (
            <div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ background: TEAL_HEADER, color: "#fff", padding: "3px 8px", fontSize: 11, fontWeight: 600, textAlign: "center" }}>
                    Liste des refus émis
                  </div>
                  {renderGrid(refus, GRID_COLS_LISTE, listeSelIdx, setListeSelIdx, "refus-liste", 320)}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 22 }}>
                  <button className="win-btn" onClick={doOuvrir} disabled={listeSelIdx < 0} data-testid="btn-refus-liste-ouvrir">Ouvrir</button>
                  {canSupprimer && (
                    <button className="win-btn" onClick={() => setShowPwdSuppr(true)} disabled={listeSelIdx < 0} data-testid="btn-refus-liste-supprimer">Supprimer</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal password suppression */}
        {showPwdSuppr && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: "#dbe6f0", padding: 20, border: "1px solid #808080", minWidth: 340 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Suppression protégée — Refus</div>
              <label style={{ fontSize: 12 }}>Mot de passe régional :</label>
              <input type="password" value={pwdSuppr}
                     onChange={e => setPwdSuppr(e.target.value)}
                     data-testid="refus-pwd-suppr-input"
                     style={{ width: "100%", padding: "3px 5px", border: "1px solid #808080", fontSize: 12, marginTop: 4 }} />
              <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="win-btn" onClick={() => { setShowPwdSuppr(false); setPwdSuppr(""); }} data-testid="btn-refus-pwd-annuler">Annuler</button>
                <button className="win-btn" onClick={doSupprimer} data-testid="btn-refus-pwd-continuer">Continuer</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Entente de paiement (parité frmRefus.showEntente) — réutilise EntenteModal
          avec source="refus" (contexte + indicateur côté serveur adaptés). */}
      {enteneModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <EntenteModal
            noref={enteneModal.noref}
            regbur={enteneModal.regbur}
            source="refus"
            showPopup={showPopup}
            onClose={() => setEnteneModal(null)}
            onSaved={() => { setField("adreentente", 1); loadRefus(); }}
          />
        </div>
      )}
    </div>
  );
};

export default RefusModal;
